import React from 'react';
import { AxiosError } from 'axios';
import { Typography } from '@one-thd/sui-atomic-components';
import { Alert } from '../../shared/alert/alert';
import { Card } from '../../shared/card/card';
import { getLastModifiedDate } from '../../../utils/date';
import {
  IPaymentCardCommonData,
  TPaymentErrorResponse,
} from '../../../models/common/payments';
import { MeatballsMenu } from '../../shared/meatballs-menu/meatballs-menu';
import { CardType } from '../../../constants/payments';
import { getPaymentsErrorCode } from '../../../utils/payments';
import { ERR_UNABLE_TO_SAVE } from '../../../constants/response-error-messages';
import {
  DELETED_ACCOUNT_MSG,
  CHANGES_SAVED_MSG,
  DELETED_CARD_MSG,
} from '../../../constants/response-success-messages';
import { pushErrorEvent } from '../../../utils/analytics';
import { IB2BPaymentCard } from '../../../models/b2b/payments';
import { usePayments } from '../../../hooks/use-payments';
import { useCustomerInfo } from '../../../hooks/use-customerInfo';
import { useMeatballMenuActions } from '../../../hooks/use-meatball-payment-actions';
import { CustomerRole } from '../../../models/common/customer';

interface ICheckingAccountCardProps {
  card: IPaymentCardCommonData;
  index: number;
  isMobile: boolean;
  setDeletedCreditCardIndex: (index: number) => void;
  onActionSuccess: (paymentId: string, message: string) => void;
  onActionFailure: (paymentId: string, message: string) => void;
}

export const CheckingAccountCard = ({
  card,
  isMobile,
  index,
  setDeletedCreditCardIndex,
  onActionFailure,
  onActionSuccess,
}: ICheckingAccountCardProps) => {
  const { alertMessage, setAlertMessage } = usePayments();
  const { role } = useCustomerInfo();

  const isAdmin = role === CustomerRole.ADMIN;
  const isCardAddedByPurchaser = card.level === 'user';
  const { checkingAccounts } = usePayments();
  const { onViewPurchases, onEditNickName, onDelete } =
    useMeatballMenuActions();

  const isHiddenForAdmin = isAdmin && isCardAddedByPurchaser;

  const handleEditNickName = () => {
    const currentCard = (
      checkingAccounts?.response as Array<IB2BPaymentCard>
    ).find((payment) => payment.paymentId === card.paymentId);

    const onSuccess = () => {
      onActionSuccess(card.paymentId, CHANGES_SAVED_MSG.message);
    };

    const onFailure = (error: AxiosError<TPaymentErrorResponse>) => {
      const errorCode = getPaymentsErrorCode(error);
      onActionFailure(card.paymentId, ERR_UNABLE_TO_SAVE.message);
      pushErrorEvent(
        'edit checking account',
        'inline',
        errorCode || 'n/a',
        ERR_UNABLE_TO_SAVE.message,
      );
    };

    onEditNickName(
      card,
      currentCard,
      CardType.CHECKING_ACCOUNT,
      onSuccess,
      onFailure,
    );
  };

  const handleDeleteCard = () => {
    const onSuccess = () => {
      onActionSuccess(card.paymentId, DELETED_ACCOUNT_MSG.message);
    };
    const onFailure = (error: AxiosError<TPaymentErrorResponse>) => {
      const errorCode = getPaymentsErrorCode(error);
      onActionFailure(card.paymentId, ERR_UNABLE_TO_SAVE.message);
      pushErrorEvent(
        'checking accounts: delete',
        'inline',
        errorCode || 'n/a',
        ERR_UNABLE_TO_SAVE.message,
      );
    };
    onDelete(card, CardType.CHECKING_ACCOUNT, onSuccess, onFailure, () => {
      setDeletedCreditCardIndex(index);
    });
  };

  const menuItems = [
    {
      value: 'Add/Edit Nickname',
      action: handleEditNickName,
      isHidden: !card.actions?.edit,
    },
    {
      value: 'View Purchases',
      action: () => {
        onViewPurchases(card, CardType.CHECKING_ACCOUNT);
      },
      isDisabled: !card.actions?.viewPurchases,
    },
    {
      value: 'Delete',
      action: handleDeleteCard,
      isHidden: isHiddenForAdmin,
    },
  ];

  const isDeleteSuccessMessage =
    alertMessage?.text === DELETED_CARD_MSG.message;

  const mobileCardAlertMessage = isMobile &&
    alertMessage?.paymentId === card.paymentId &&
    !isDeleteSuccessMessage && (
      <div className="sui-mb-4">
        <Alert
          status={alertMessage.alertStatus}
          onClose={() => setAlertMessage(null)}
        >
          {alertMessage.text}
        </Alert>
      </div>
    );

  return (
    <>
      {mobileCardAlertMessage}
      <Card
        isDefault={false}
        isInactive={false}
        defaultLabel="Default Payment Method"
      >
        <div
          className={`sui-flex sui-justify-between sui-align-center ${
            card.cardNickName ? 'sui-mb-2' : 'sui-mb-3'
          }`}
        >
          <h3
            className="sui-font-display sui-text-3xl sui-mr-4 sui-truncate sui-text-primary"
            aria-label="nickname"
          >
            {card.cardNickName}
          </h3>
          <div>
            <MeatballsMenu label="Payment" items={menuItems} />
          </div>
        </div>
        <div className="sui-mb-6">
          <Typography variant="body-lg" color="subtle">
            Account ****{card.cardNumberLast4}
          </Typography>
        </div>
        {card.addedBy && (
          <div className="sui-overflow-hidden">
            <Typography truncate color="subtle">
              Added by <span data-qm-mask="">{card.addedBy}</span>
            </Typography>
          </div>
        )}
        {card.lastModifiedDate && (
          <Typography color="subtle">
            Last edited {getLastModifiedDate(card.lastModifiedDate)}
          </Typography>
        )}
      </Card>
    </>
  );
};

CheckingAccountCard.displayName = 'CheckingAccountCard';
